/**
 * Leep Chas — Quiz Interativo
 * Web Component <leep-quiz> com scoring engine, persistencia multi-camada
 * e integracoes (Make webhook, cart attributes, dataLayer).
 *
 * Carregamento async do config:
 *   1. Busca quiz-config.json via fetch (URL em window.LEEP_QUIZ_CONFIG_URL)
 *   2. Cacheia em sessionStorage por leep_quiz_config_v1
 *   3. Renderiza o quiz apos receber a config
 */

const QUIZ_CONFIG_CACHE_KEY = 'leep_quiz_config_v1';

async function loadQuizConfig() {
  try {
    const cached = sessionStorage.getItem(QUIZ_CONFIG_CACHE_KEY);
    if (cached) return JSON.parse(cached);

    const url = window.LEEP_QUIZ_CONFIG_URL;
    if (!url) throw new Error('LEEP_QUIZ_CONFIG_URL not defined');

    const res  = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const text = await res.text();
    sessionStorage.setItem(QUIZ_CONFIG_CACHE_KEY, text);
    return JSON.parse(text);
  } catch (err) {
    console.error('[LeepQuiz] Config load failed:', err);
    return null;
  }
}

class LeepQuiz extends HTMLElement {
  constructor() {
    super();
    this._config   = null;
    this._products = null;
    this._state    = null;
  }

  async connectedCallback() {
    const section    = this.closest('.quiz-section');
    const productsEl = section?.querySelector('#quiz-products-data');
    if (!productsEl) { console.error('[LeepQuiz] #quiz-products-data not found'); return; }

    try {
      this._products = JSON.parse(productsEl.textContent);
    } catch (e) {
      console.error('[LeepQuiz] Failed to parse products JSON:', e);
      return;
    }

    this._config = await loadQuizConfig();
    if (!this._config) return;

    this.webhookUrl      = this.dataset.webhookUrl      || '';
    this.discountCode    = this.dataset.discountCode    || 'QUIZ10';
    this.freeShippingQty = parseInt(this.dataset.freeShippingQty || '3', 10);

    this._state = this._loadState() || this._initState();

    if (this._state.completed && this._state.result) {
      this._state.screen = 'result';
    }

    this._render();
  }

  // ─── State ───────────────────────────────────────────────────────────────

  _initState() {
    return {
      quiz_id:      this._uuid(),
      started_at:   new Date().toISOString(),
      screen:       'welcome',
      current_step: 0,
      answers:      {},
      customer:     null,
      result:       null,
      completed:    false,
    };
  }

  _loadState() {
    try {
      const raw = localStorage.getItem('leep_quiz_state');
      if (!raw) return null;
      const s   = JSON.parse(raw);
      const age = Date.now() - new Date(s.started_at).getTime();
      if (age > 30 * 24 * 60 * 60 * 1000) return null;
      return s;
    } catch { return null; }
  }

  _saveState() {
    try {
      localStorage.setItem('leep_quiz_state', JSON.stringify(this._state));
    } catch {}
  }

  _uuid() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = Math.random() * 16 | 0;
      return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
  }

  // ─── Render ──────────────────────────────────────────────────────────────

  _render(direction = 'forward') {
    const { screen } = this._state;
    let html = '';

    switch (screen) {
      case 'welcome':     html = this._tplWelcome();     break;
      case 'question':    html = this._tplQuestion();    break;
      case 'capture':     html = this._tplCapture();     break;
      case 'calculating': html = this._tplCalculating(); break;
      case 'result':      html = this._tplResult();      break;
      default:            html = this._tplWelcome();
    }

    this.innerHTML = html;
    this._bindEvents();
    this._animateIn(direction);

    if (screen === 'calculating') this._runCalculating();
  }

  _transition(mutate, direction = 'forward') {
    const screen = this.querySelector('.lq-screen');
    if (screen && window.gsap) {
      const yOut = direction === 'forward' ? -24 : 24;
      gsap.to(screen, {
        opacity: 0, y: yOut, duration: 0.22, ease: 'power2.in',
        onComplete: () => { mutate(); this._render(direction); },
      });
    } else {
      mutate();
      this._render(direction);
    }
  }

  _animateIn(direction) {
    const screen = this.querySelector('.lq-screen');
    if (!screen || !window.gsap) return;
    const yFrom = direction === 'forward' ? 28 : -28;
    gsap.fromTo(screen,
      { opacity: 0, y: yFrom },
      { opacity: 1, y: 0, duration: 0.38, ease: 'power3.out' }
    );
  }

  // ─── Templates ───────────────────────────────────────────────────────────

  _tplWelcome() {
    return `
      <div class="lq-screen lq-welcome" role="main">
        <div class="lq-welcome__inner">
          <span class="lq-badge">Quiz Personalizado</span>
          <h1 class="lq-welcome__title">Descubra o cha ideal pra voce em 60 segundos</h1>
          <p class="lq-welcome__sub">
            6 perguntas rapidas. No final, recomendamos seu cha + cupom de 20%.
          </p>
          <button class="lq-btn lq-btn--primary lq-welcome__cta" data-action="start" autofocus>
            COMECAR
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </button>
          <ul class="lq-welcome__pills" aria-label="Garantias">
            <li>Gratis</li>
            <li>Sem compromisso</li>
            <li>Resultado instantaneo</li>
          </ul>
        </div>
      </div>
    `;
  }

  _tplQuestion() {
    const q        = this._config.questions[this._state.current_step];
    const total    = this._config.questions.length;
    const isMulti  = q.type === 'multi';
    const maxSel   = q.max_select || 4;
    const selected = isMulti ? (this._state.answers[q.id] || []) : null;

    const options = q.options.map(opt => {
      const isActive = isMulti
        ? selected.includes(opt.value)
        : this._state.answers[q.id] === opt.value;
      const atMax = isMulti && !isActive && selected.length >= maxSel;

      return `
        <button
          class="lq-option${isActive ? ' is-selected' : ''}"
          data-action="${isMulti ? 'toggle-multi' : 'answer-single'}"
          data-question="${q.id}"
          data-value="${opt.value}"
          aria-pressed="${isActive}"
          ${atMax ? 'disabled' : ''}
        >
          ${opt.emoji ? `<span class="lq-option__emoji" aria-hidden="true">${opt.emoji}</span>` : ''}
          <span class="lq-option__label">${opt.label}</span>
          <span class="lq-option__check" aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </span>
        </button>
      `;
    }).join('');

    return `
      <div class="lq-screen lq-question">
        ${this._tplProgress(this._state.current_step, total)}
        <div class="lq-question__nav">
          <span class="lq-question__step">${this._state.current_step + 1} de ${total}</span>
          <button class="lq-back" data-action="prev" aria-label="Voltar para pergunta anterior">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Voltar
          </button>
        </div>
        <h2 class="lq-question__title">${q.title}</h2>
        ${q.subtitle ? `<p class="lq-question__subtitle">${q.subtitle}</p>` : ''}
        <div
          class="lq-options${isMulti ? ' lq-options--chips' : ' lq-options--cards'}"
          role="group"
          aria-label="Opcoes da pergunta ${this._state.current_step + 1}"
        >
          ${options}
        </div>
        ${isMulti ? `
          <button
            class="lq-btn lq-btn--primary lq-continue"
            data-action="continue-multi"
            data-question="${q.id}"
            ${(this._state.answers[q.id] || []).length === 0 ? 'disabled' : ''}
          >
            CONTINUAR
          </button>
        ` : ''}
      </div>
    `;
  }

  _tplCapture() {
    const total = this._config.questions.length;
    const c     = this._state.customer || {};
    return `
      <div class="lq-screen lq-capture">
        ${this._tplProgress(total, total)}
        <div class="lq-question__nav">
          <span class="lq-question__step">${total + 1} de ${total + 1}</span>
          <button class="lq-back" data-action="prev" aria-label="Voltar">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Voltar
          </button>
        </div>
        <h2 class="lq-capture__title">Quase la! Onde mandamos seu resultado + cupom de 20%?</h2>
        <form class="lq-capture__form" data-form="capture" novalidate>
          <div class="lq-field">
            <label class="lq-field__label" for="lq-name">Seu nome</label>
            <input
              id="lq-name" name="name" type="text"
              class="lq-input" placeholder="Maria"
              autocomplete="given-name"
              value="${this._esc(c.first_name || '')}"
            >
          </div>
          <div class="lq-field">
            <label class="lq-field__label" for="lq-email">
              E-mail <span class="lq-required" aria-label="obrigatorio">*</span>
            </label>
            <input
              id="lq-email" name="email" type="email"
              class="lq-input" placeholder="maria@email.com"
              autocomplete="email"
              required
              value="${this._esc(c.email || '')}"
            >
            <span class="lq-field__error" id="lq-email-error" role="alert" aria-live="assertive" hidden></span>
          </div>
          <div class="lq-field">
            <label class="lq-field__label" for="lq-phone">WhatsApp <span class="lq-optional">(opcional)</span></label>
            <input
              id="lq-phone" name="phone" type="tel"
              class="lq-input" placeholder="+55 11 99999-9999"
              autocomplete="tel"
              value="${this._esc(c.phone || '')}"
            >
          </div>
          <label class="lq-checkbox">
            <input type="checkbox" name="accepts_marketing" ${c.accepts_marketing !== false ? 'checked' : ''}>
            <span>Quero receber dicas e ofertas exclusivas</span>
          </label>
          <button type="submit" class="lq-btn lq-btn--primary lq-capture__submit">
            VER MEU RESULTADO
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </button>
          <p class="lq-capture__disclaimer">
            Seus dados estao seguros. Nao compartilhamos com terceiros.
            <a href="/pages/privacidade" target="_blank" rel="noopener">Politica de privacidade</a>
          </p>
        </form>
      </div>
    `;
  }

  _tplCalculating() {
    return `
      <div class="lq-screen lq-calculating" aria-live="polite" aria-busy="true">
        <div class="lq-spinner" aria-hidden="true">
          <div class="lq-spinner__ring"></div>
        </div>
        <p class="lq-calculating__msg" id="lq-calc-msg">Analisando seu perfil...</p>
      </div>
    `;
  }

  _tplResult() {
    const { result } = this._state;
    if (!result) return this._tplWelcome();
    return result.type === 'combo'
      ? this._tplCombo(result)
      : this._tplSingle(result);
  }

  _tplSingle(result) {
    const cfg       = this._config.products[result.product_key];
    const pData     = this._products[cfg.handle] || {};
    const reasoning = this._buildReasoning(cfg);
    const price     = pData.price     ? this._formatBRL(pData.price)     : '';
    const install   = pData.price     ? this._formatBRL(pData.price / 6) : '';
    const imgHtml   = pData.image
      ? `<img src="${this._esc(pData.image)}" alt="${this._esc(cfg.title)}" class="lq-result__img" width="480" height="480" loading="lazy">`
      : `<div class="lq-result__img-placeholder" aria-hidden="true"></div>`;

    return `
      <div class="lq-screen lq-result lq-result--single">
        <div class="lq-result__hero">
          <span class="lq-badge">Seu cha ideal e:</span>
          <h2 class="lq-result__product-name">${this._esc(cfg.title)}</h2>
          <p class="lq-result__tagline">${this._esc(cfg.tagline)}</p>
        </div>

        <div class="lq-result__card">
          <div class="lq-result__img-wrap">${imgHtml}</div>
          <div class="lq-result__info">
            <div class="lq-stars" aria-label="4.9 de 5 estrelas" aria-hidden="true">&#9733;&#9733;&#9733;&#9733;&#9733;</div>
            ${price ? `
              <div class="lq-result__price-block">
                <span class="lq-result__price">${price}</span>
                <span class="lq-result__installment">6x de ${install} sem juros</span>
              </div>
            ` : ''}
            <div class="lq-coupon">
              <span class="lq-coupon__label">Cupom:</span>
              <strong class="lq-coupon__code">${this._esc(this.discountCode)}</strong>
              <span class="lq-coupon__desc">20% OFF no 1o pedido</span>
            </div>
            <div class="lq-result__actions">
              <button
                class="lq-btn lq-btn--primary"
                data-action="buy"
                data-variant="${pData.variant_id || ''}"
                data-handle="${this._esc(cfg.handle)}"
                data-qty="1"
                ${!pData.variant_id ? 'disabled' : ''}
              >COMPRAR AGORA</button>
              ${pData.url ? `<a href="${this._esc(pData.url)}" class="lq-btn lq-btn--ghost">Ver detalhes</a>` : ''}
            </div>
          </div>
        </div>

        ${reasoning.length > 0 ? `
          <div class="lq-result__reasoning">
            <h3 class="lq-result__reasoning-title">Por que recomendamos pra voce:</h3>
            <ul class="lq-result__reasoning-list">
              ${reasoning.map(r => `<li>${this._esc(r)}</li>`).join('')}
            </ul>
          </div>
        ` : ''}

        <div class="lq-upsell">
          <p class="lq-upsell__text">
            Comprando <strong>${this.freeShippingQty} unidades</strong> voce ganha <strong>frete gratis!</strong>
          </p>
          <button
            class="lq-btn lq-btn--secondary"
            data-action="buy"
            data-variant="${pData.variant_id || ''}"
            data-handle="${this._esc(cfg.handle)}"
            data-qty="${this.freeShippingQty}"
            ${!pData.variant_id ? 'disabled' : ''}
          >
            ADICIONAR ${this.freeShippingQty} UNIDADES + 20% OFF
          </button>
        </div>

        <button class="lq-copy-coupon" data-action="copy-coupon" aria-live="polite">
          <span data-copy-label>Copiar cupom de desconto</span>
        </button>

        <button class="lq-restart" data-action="restart">Refazer o quiz</button>
      </div>
    `;
  }

  _tplCombo(result) {
    const cards = (result.product_keys || []).map(key => {
      const cfg   = this._config.products[key];
      const pData = this._products[cfg.handle] || {};
      return { cfg, pData, price: pData.price ? this._formatBRL(pData.price) : '' };
    });

    const cardsHtml = cards.map(({ cfg, pData, price }) => `
      <div class="lq-combo-card">
        ${pData.image
          ? `<img src="${this._esc(pData.image)}" alt="${this._esc(cfg.title)}" class="lq-combo-card__img" width="300" height="300" loading="lazy">`
          : `<div class="lq-combo-card__placeholder" aria-hidden="true"></div>`}
        <div class="lq-combo-card__body">
          <h3 class="lq-combo-card__title">${this._esc(cfg.title)}</h3>
          <p class="lq-combo-card__tagline">${this._esc(cfg.tagline)}</p>
          ${price ? `<span class="lq-combo-card__price">${price}</span>` : ''}
        </div>
      </div>
    `).join('');

    const v1 = cards[0]?.pData?.variant_id || '';
    const v2 = cards[1]?.pData?.variant_id || '';

    return `
      <div class="lq-screen lq-result lq-result--combo">
        <div class="lq-result__hero">
          <span class="lq-badge">Recomendamos o combo:</span>
          <p class="lq-result__combo-sub">Juntos potencializam o efeito para um resultado mais completo</p>
        </div>

        <div class="lq-combo-grid">${cardsHtml}</div>

        <div class="lq-coupon">
          <span class="lq-coupon__label">Cupom:</span>
          <strong class="lq-coupon__code">${this._esc(this.discountCode)}</strong>
          <span class="lq-coupon__desc">20% OFF</span>
        </div>

        <button
          class="lq-btn lq-btn--primary"
          data-action="buy-combo"
          data-v1="${v1}"
          data-v2="${v2}"
          ${!v1 && !v2 ? 'disabled' : ''}
        >COMPRAR COMBO</button>

        <button class="lq-copy-coupon" data-action="copy-coupon" aria-live="polite">
          <span data-copy-label>Copiar cupom de desconto</span>
        </button>

        <button class="lq-restart" data-action="restart">Refazer o quiz</button>
      </div>
    `;
  }

  _tplProgress(current, total) {
    const pct = total > 0 ? Math.round((current / total) * 100) : 0;
    return `
      <div class="lq-progress" role="progressbar" aria-valuenow="${pct}" aria-valuemin="0" aria-valuemax="100" aria-label="Progresso: ${pct}%">
        <div class="lq-progress__bar" style="width: ${pct}%"></div>
      </div>
    `;
  }

  // ─── Events ──────────────────────────────────────────────────────────────

  _bindEvents() {
    this.querySelectorAll('[data-action]').forEach(el => {
      el.addEventListener('click', e => this._onAction(e.currentTarget));
    });
    const form = this.querySelector('[data-form="capture"]');
    if (form) form.addEventListener('submit', e => { e.preventDefault(); this._handleCapture(form); });
  }

  _onAction(el) {
    const action = el.dataset.action;

    if (action === 'start') {
      this._transition(() => {
        this._state.screen       = 'question';
        this._state.current_step = 0;
        this._saveState();
      });
      return;
    }

    if (action === 'answer-single') {
      const { question, value } = el.dataset;
      this._state.answers[question] = value;
      el.classList.add('is-selected');
      el.setAttribute('aria-pressed', 'true');
      this._pulseOption(el);
      setTimeout(() => {
        const next = this._state.current_step + 1;
        this._transition(() => {
          this._state.current_step = next;
          if (next >= this._config.questions.length) this._state.screen = 'capture';
          this._saveState();
        });
      }, 300);
      return;
    }

    if (action === 'toggle-multi') {
      const { question, value } = el.dataset;
      const q       = this._config.questions.find(q => q.id === question);
      const maxSel  = q?.max_select || 4;
      const arr     = [...(this._state.answers[question] || [])];
      const idx     = arr.indexOf(value);

      if (idx === -1) {
        if (arr.length >= maxSel) return;
        arr.push(value);
        el.classList.add('is-selected');
        el.setAttribute('aria-pressed', 'true');
        this._pulseOption(el);
      } else {
        arr.splice(idx, 1);
        el.classList.remove('is-selected');
        el.setAttribute('aria-pressed', 'false');
      }

      this._state.answers[question] = arr;
      this._saveState();

      this.querySelectorAll(`[data-question="${question}"]`).forEach(opt => {
        if (!opt.classList.contains('is-selected')) opt.disabled = arr.length >= maxSel;
      });

      const continueBtn = this.querySelector('.lq-continue');
      if (continueBtn) continueBtn.disabled = arr.length === 0;
      return;
    }

    if (action === 'continue-multi') {
      const next = this._state.current_step + 1;
      this._transition(() => {
        this._state.current_step = next;
        if (next >= this._config.questions.length) this._state.screen = 'capture';
        this._saveState();
      });
      return;
    }

    if (action === 'prev') {
      this._transition(() => {
        if (this._state.screen === 'capture') {
          this._state.screen       = 'question';
          this._state.current_step = this._config.questions.length - 1;
        } else if (this._state.current_step > 0) {
          this._state.current_step--;
        } else {
          this._state.screen = 'welcome';
        }
        this._saveState();
      }, 'back');
      return;
    }

    if (action === 'buy') {
      this._addToCart(el.dataset.variant, parseInt(el.dataset.qty || '1', 10), el.dataset.handle, el);
      return;
    }

    if (action === 'buy-combo') {
      const items = [el.dataset.v1, el.dataset.v2]
        .filter(Boolean)
        .map(id => ({ id: parseInt(id, 10), quantity: 1 }));
      this._addComboToCart(items, el);
      return;
    }

    if (action === 'copy-coupon') {
      const code  = this.discountCode;
      const label = el.querySelector('[data-copy-label]') || el;
      const orig  = label.textContent;
      const done  = () => {
        label.textContent = 'Copiado!';
        el.classList.add('is-copied');
        setTimeout(() => {
          label.textContent = orig;
          el.classList.remove('is-copied');
        }, 2200);
      };
      if (navigator.clipboard) {
        navigator.clipboard.writeText(code).then(done).catch(() => {
          try {
            const ta = document.createElement('textarea');
            ta.value = code; ta.style.cssText = 'position:fixed;opacity:0';
            document.body.appendChild(ta); ta.select(); document.execCommand('copy');
            document.body.removeChild(ta); done();
          } catch {}
        });
      } else {
        try {
          const ta = document.createElement('textarea');
          ta.value = code; ta.style.cssText = 'position:fixed;opacity:0';
          document.body.appendChild(ta); ta.select(); document.execCommand('copy');
          document.body.removeChild(ta); done();
        } catch {}
      }
      return;
    }

    if (action === 'restart') {
      localStorage.removeItem('leep_quiz_state');
      this._state = this._initState();
      this._transition(() => {});
      return;
    }
  }

  _pulseOption(el) {
    if (!window.gsap) return;
    gsap.timeline()
      .to(el, { scale: 1.04, duration: 0.12, ease: 'power1.out' })
      .to(el, { scale: 1,    duration: 0.18, ease: 'power2.out' });
  }

  // ─── Capture ─────────────────────────────────────────────────────────────

  _handleCapture(form) {
    const emailEl = form.querySelector('[name="email"]');
    const email   = emailEl.value.trim();
    const errEl   = form.querySelector('#lq-email-error');

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      if (errEl) { errEl.textContent = 'Informe um e-mail valido.'; errEl.hidden = false; }
      emailEl.focus();
      return;
    }
    if (errEl) errEl.hidden = true;

    this._state.customer = {
      email,
      first_name:        form.querySelector('[name="name"]').value.trim(),
      phone:             form.querySelector('[name="phone"]').value.trim(),
      accepts_marketing: form.querySelector('[name="accepts_marketing"]').checked,
    };
    this._saveState();
    this._transition(() => { this._state.screen = 'calculating'; });
  }

  // ─── Calculating ─────────────────────────────────────────────────────────

  _runCalculating() {
    const msgs = [
      'Analisando seu perfil...',
      'Identificando sintomas...',
      'Cruzando com nossa base...',
      'Selecionando o cha ideal...',
    ];
    let i = 0;
    const tick = setInterval(() => {
      i++;
      const el = document.getElementById('lq-calc-msg');
      if (el && msgs[i]) el.textContent = msgs[i];
      if (i >= msgs.length - 1) clearInterval(tick);
    }, 600);

    setTimeout(() => {
      clearInterval(tick);
      const result      = this._calculateResult();
      this._state.result    = result;
      this._state.completed = true;
      this._saveState();

      this._sendWebhook(result);
      this._updateCartAttributes(result);
      this._pushDataLayer(result);

      this._transition(() => { this._state.screen = 'result'; });
    }, 2700);
  }

  // ─── Scoring ─────────────────────────────────────────────────────────────

  _calculateResult() {
    const score   = { digest: 0, slim: 0, diuret: 0 };
    const scoring = this._config.scoring;

    for (const [qId, val] of Object.entries(this._state.answers)) {
      if (!scoring[qId]) continue;
      const values = Array.isArray(val) ? val : [val];
      values.forEach(v => {
        const pts = scoring[qId]?.[v];
        if (!pts) return;
        score.digest += pts.digest || 0;
        score.slim   += pts.slim   || 0;
        score.diuret += pts.diuret || 0;
      });
    }

    const ranked = Object.entries(score).sort((a, b) => b[1] - a[1]);
    const winner = ranked[0];
    const runner = ranked[1];
    const gap    = winner[1] - runner[1];

    if (gap < 2 && runner[1] > 0) {
      return {
        type:                'combo',
        product_keys:        [winner[0], runner[0]],
        score,
        recommendation_type: 'combo',
      };
    }

    const key = winner[0];
    const cfg = this._config.products[key];
    return {
      type:                'single',
      product_key:         key,
      product_handle:      cfg.handle,
      product_title:       cfg.title,
      score,
      discount_code:       this.discountCode,
      recommendation_type: 'single',
    };
  }

  _buildReasoning(productConfig) {
    const reasons = [];
    const labels  = this._config.reasoning_labels;
    const keys    = productConfig.reasoning_keys;

    for (const [, val] of Object.entries(this._state.answers)) {
      const values = Array.isArray(val) ? val : [val];
      values.forEach(v => {
        if (keys.includes(v) && labels[v] && !reasons.includes(labels[v])) {
          reasons.push(labels[v]);
        }
      });
    }
    return reasons.slice(0, 3);
  }

  // ─── Integrations ────────────────────────────────────────────────────────

  async _sendWebhook(result) {
    if (!this.webhookUrl) return;
    const payload = {
      event:        'quiz_completed',
      quiz_id:      this._state.quiz_id,
      completed_at: new Date().toISOString(),
      customer:     this._state.customer,
      answers:      this._state.answers,
      result: {
        product_handle:      result.product_handle || null,
        product_title:       result.product_title  || null,
        product_keys:        result.product_keys   || null,
        score:               result.score,
        discount_code:       this.discountCode,
        recommendation_type: result.recommendation_type,
      },
      metadata: {
        user_agent:   navigator.userAgent,
        utm_source:   new URLSearchParams(location.search).get('utm_source'),
        utm_medium:   new URLSearchParams(location.search).get('utm_medium'),
        utm_campaign: new URLSearchParams(location.search).get('utm_campaign'),
        referrer:     document.referrer,
        device_type:  window.matchMedia('(max-width: 768px)').matches ? 'mobile' : 'desktop',
      },
    };

    try {
      await fetch(this.webhookUrl, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(payload),
      });
    } catch (err) {
      console.warn('[LeepQuiz] Webhook failed — queued for retry:', err);
      try {
        const q = JSON.parse(localStorage.getItem('leep_quiz_pending') || '[]');
        q.push({ url: this.webhookUrl, payload, ts: Date.now() });
        localStorage.setItem('leep_quiz_pending', JSON.stringify(q.slice(-3)));
      } catch {}
    }
  }

  async _updateCartAttributes(result) {
    try {
      await fetch('/cart/update.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            'Quiz_ID':          this._state.quiz_id,
            'Quiz_Resultado':   result.product_title || (result.product_keys || []).join(' + '),
            'Quiz_Objetivo':    this._state.answers.objetivo || '',
            'Quiz_Sintomas':    (this._state.answers.sintomas || []).join(', '),
            'Quiz_FaixaEtaria': this._state.answers.faixa_etaria || '',
          },
        }),
      });
    } catch (err) {
      console.warn('[LeepQuiz] Cart attributes failed:', err);
    }
  }

  _pushDataLayer(result) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event:               'quiz_completed',
      quiz_id:             this._state.quiz_id,
      recommended_product: result.product_handle || 'combo',
      quiz_objetivo:       this._state.answers.objetivo,
      quiz_score_digest:   result.score.digest,
      quiz_score_slim:     result.score.slim,
      quiz_score_diuret:   result.score.diuret,
    });
  }

  // ─── Cart ────────────────────────────────────────────────────────────────

  async _addToCart(variantId, qty, handle, triggerEl) {
    if (!variantId) { window.location.href = `/products/${handle}`; return; }

    this._setBtnLoading(triggerEl, true);
    try {
      const res = await fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: parseInt(variantId, 10), quantity: qty,
            properties: { '_quiz_id': this._state.quiz_id, '_recomendado': 'true' },
          }],
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      document.dispatchEvent(new CustomEvent('leep:cart-open'));
      document.dispatchEvent(new CustomEvent('leep:cart-refresh'));
    } catch (err) {
      console.error('[LeepQuiz] Add to cart failed:', err);
      this._setBtnLoading(triggerEl, false, 'Erro — tente novamente');
    }
  }

  async _addComboToCart(items, triggerEl) {
    if (!items.length) return;
    this._setBtnLoading(triggerEl, true);
    try {
      const res = await fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: items.map(i => ({
            ...i,
            properties: { '_quiz_id': this._state.quiz_id, '_recomendado': 'combo' },
          })),
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      document.dispatchEvent(new CustomEvent('leep:cart-open'));
      document.dispatchEvent(new CustomEvent('leep:cart-refresh'));
    } catch (err) {
      console.error('[LeepQuiz] Combo add to cart failed:', err);
      this._setBtnLoading(triggerEl, false, 'Erro — tente novamente');
    }
  }

  _setBtnLoading(el, loading, errorText) {
    if (!el) return;
    el.disabled = loading;
    if (loading) {
      el._originalText = el.textContent;
      el.textContent   = 'Adicionando...';
    } else {
      el.textContent = errorText || el._originalText || 'Comprar';
    }
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────

  _formatBRL(cents) {
    return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  _esc(str) {
    return String(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
}

customElements.define('leep-quiz', LeepQuiz);
